import { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    Divider,
    Chip,
    LinearProgress,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DevicesIcon from "@mui/icons-material/Devices";

const BLOCK_DURATION_MS = 2 * 60 * 1000; // 2 minutes

function pad(n) {
    return String(n).padStart(2, "0");
}

function formatDateTime(date) {
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatCountdown(ms) {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export default function ConnectionBlockedPage() {
    const [blockedAt] = useState(() => Date.now());
    const [unblockAt] = useState(() => Date.now() + BLOCK_DURATION_MS);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const remaining = unblockAt - now;
    const progress = Math.min(100, ((now - blockedAt) / BLOCK_DURATION_MS) * 100);
    const isExpired = remaining <= 0;

    return (
        <Box
            sx={{
                minHeight: "100vh",
                background: "#000000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
                fontFamily: "'Orbitron', sans-serif",
            }}
        >
            <Paper
                elevation={24}
                sx={{
                    maxWidth: 520,
                    width: "100%",
                    borderRadius: 3,
                    overflow: "hidden",
                    background: "#0a0a0a",
                    border: "1px solid #1565c0",
                    color: "#fff",
                    fontFamily: "'Orbitron', sans-serif",
                }}
            >
                {/* Blue top bar */}
                <Box
                    sx={{
                        background: "#1565c0",
                        py: 2.5,
                        px: 3,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <BlockIcon sx={{ fontSize: 26, color: "#fff" }} />
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            letterSpacing: 2,
                            fontFamily: "'Orbitron', sans-serif",
                            color: "#fff",
                            fontSize: "1rem",
                        }}
                    >
                        ACCESS SUSPENDED
                    </Typography>
                </Box>

                <Box sx={{ px: 4, py: 4 }}>
                    {/* Icon */}
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                        <DevicesIcon
                            sx={{
                                fontSize: 64,
                                color: "#1565c0",
                                filter: "drop-shadow(0 0 10px rgba(21,101,192,0.7))",
                            }}
                        />
                    </Box>

                    {/* Title */}
                    <Typography
                        variant="h5"
                        sx={{
                            textAlign: "center",
                            fontWeight: 700,
                            mb: 1,
                            fontFamily: "'Orbitron', sans-serif",
                            color: "#1e88e5",
                            fontSize: "1.1rem",
                            letterSpacing: 1,
                        }}
                    >
                        TOO MANY ACTIVE CONNECTIONS
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            textAlign: "center",
                            color: "rgba(255,255,255,0.6)",
                            mb: 3,
                            lineHeight: 1.8,
                            fontFamily: "'Orbitron', sans-serif",
                            fontSize: "0.7rem",
                            letterSpacing: 0.5,
                        }}
                    >
                        Your account has exceeded the maximum number of simultaneous
                        connections. All sessions have been closed for security reasons.
                    </Typography>

                    <Divider sx={{ borderColor: "#1565c0", mb: 3, opacity: 0.4 }} />

                    {/* Countdown */}
                    <Box
                        sx={{
                            background: "#0d0d0d",
                            border: "1px solid #1565c0",
                            borderRadius: 2,
                            p: 2.5,
                            mb: 3,
                            textAlign: "center",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <AccessTimeIcon sx={{ color: "#1e88e5", fontSize: 16 }} />
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "rgba(255,255,255,0.5)",
                                    letterSpacing: 2,
                                    textTransform: "uppercase",
                                    fontFamily: "'Orbitron', sans-serif",
                                    fontSize: "0.6rem",
                                }}
                            >
                                {isExpired ? "Block Expired" : "Connections blocked for"}
                            </Typography>
                        </Box>

                        <Typography
                            sx={{
                                fontFamily: "'Orbitron', sans-serif",
                                fontWeight: 700,
                                color: isExpired ? "#fff" : "#1e88e5",
                                letterSpacing: 6,
                                fontSize: "2.5rem",
                                textShadow: isExpired
                                    ? "0 0 20px rgba(255,255,255,0.4)"
                                    : "0 0 20px rgba(30,136,229,0.6)",
                            }}
                        >
                            {isExpired ? "00:00" : formatCountdown(remaining)}
                        </Typography>

                        <LinearProgress
                            variant="determinate"
                            value={progress}
                            sx={{
                                mt: 2,
                                height: 3,
                                borderRadius: 2,
                                backgroundColor: "rgba(255,255,255,0.08)",
                                "& .MuiLinearProgress-bar": {
                                    background: "linear-gradient(90deg, #1565c0, #1e88e5)",
                                    borderRadius: 2,
                                },
                            }}
                        />

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 2,
                                gap: 1,
                                flexWrap: "wrap",
                            }}
                        >
                            <Chip
                                label={`Blocked: ${formatDateTime(new Date(blockedAt))}`}
                                size="small"
                                sx={{
                                    background: "rgba(21,101,192,0.15)",
                                    color: "rgba(255,255,255,0.5)",
                                    fontSize: "0.58rem",
                                    fontFamily: "'Orbitron', sans-serif",
                                    border: "1px solid rgba(21,101,192,0.3)",
                                }}
                            />
                            <Chip
                                label={`Until: ${formatDateTime(new Date(unblockAt))}`}
                                size="small"
                                sx={{
                                    background: "rgba(21,101,192,0.15)",
                                    color: "rgba(255,255,255,0.5)",
                                    fontSize: "0.58rem",
                                    fontFamily: "'Orbitron', sans-serif",
                                    border: "1px solid rgba(21,101,192,0.3)",
                                }}
                            />
                        </Box>
                    </Box>

                    {/* Contact admin */}
                    <Box
                        sx={{
                            background: "#0d0d0d",
                            border: "1px solid rgba(255,255,255,0.08)",
                            borderRadius: 2,
                            p: 2.5,
                            mb: 3,
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: "rgba(255,255,255,0.55)",
                                mb: 2,
                                lineHeight: 1.8,
                                fontFamily: "'Orbitron', sans-serif",
                                fontSize: "0.65rem",
                                letterSpacing: 0.5,
                            }}
                        >
                            Need more simultaneous connections? Contact your administrator to
                            increase your connection limit.
                        </Typography>

                        <Button
                            variant="outlined"
                            startIcon={<EmailIcon />}
                            href="mailto:admin@km-bizz.com?subject=Request for more connections&body=Hello, I need more simultaneous connections for my account."
                            sx={{
                                borderColor: "#1565c0",
                                color: "#fff",
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                fontFamily: "'Orbitron', sans-serif",
                                letterSpacing: 1,
                                "&:hover": {
                                    background: "rgba(21,101,192,0.2)",
                                    borderColor: "#1e88e5",
                                },
                            }}
                        >
                            admin@km-bizz.com
                        </Button>
                    </Box>

                    {/* Retry if expired */}
                    {isExpired && (
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => window.location.reload()}
                            sx={{
                                background: "#1565c0",
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 700,
                                fontFamily: "'Orbitron', sans-serif",
                                fontSize: "0.75rem",
                                letterSpacing: 2,
                                py: 1.2,
                                "&:hover": {
                                    background: "#1e88e5",
                                },
                            }}
                        >
                            TRY AGAIN
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}
